// A suggested change made by editing the whole file: the edits between the
// head's text and the edited text, and how they become what GitHub takes.
// A suggestion replaces one range of lines, at least one, all inside one
// diff hunk (lines outside the diff take no line comment). Edits in one
// hunk become one suggestion spanning them; edits in several hunks, one
// suggestion each; an edit outside every hunk can only go as a patch in a
// comment.
import type { Block } from '../engine/types';
import { myers } from '../diff/fallback';
import { formatSuggestion } from './suggestion';

/**
 * One edit: head lines `start`…`end` (1-based, inclusive; `end` is
 * `start - 1` for lines inserted before line `start`) replaced by `lines`,
 * which start at index `at` (0-based) of the edited text.
 */
export interface Region {
  start: number;
  end: number;
  lines: string[];
  at: number;
}

/** A suggested change: head lines `start`…`end` (`original`) become `lines`, found at index `at` of the edited text. */
export interface Suggestion {
  start: number;
  end: number;
  original: string[];
  lines: string[];
  at: number;
}

/** Head lines `start`…`end` (1-based, inclusive) that take line comments: one diff hunk's new side. */
export interface Hunk {
  start: number;
  end: number;
}

/** What the edits can be posted as. */
export type SuggestPlan =
  /** No edits. */
  | { kind: 'unchanged' }
  /** Every edit is in one hunk: one suggestion spans them. */
  | { kind: 'one'; suggestion: Suggestion }
  /** Every edit is in a hunk, but not the same one: a suggestion per hunk. */
  | { kind: 'split'; suggestions: Suggestion[] }
  /** Some edits (`outside`) are in no hunk: the edits (`regions`, all of them) can go only as `patch`. */
  | { kind: 'outside'; regions: Region[]; outside: Region[]; patch: string };

/** The edits that turn `head` into `edited`, in order. */
export function editRegions(head: readonly string[], edited: readonly string[]): Region[] {
  const out: Region[] = [];
  let open: Region | null = null;
  // The next head line, and the next edited line (both 1-based).
  let nextOld = 1;
  let nextNew = 1;
  for (const op of myers([...head], [...edited])) {
    if (op.kind === 'context') {
      if (open) out.push(open);
      open = null;
      nextOld = op.old! + 1;
      nextNew = op.new! + 1;
      continue;
    }
    open ??= { start: nextOld, end: nextOld - 1, lines: [], at: nextNew - 1 };
    if (op.kind === 'del') {
      open.end = op.old!;
      nextOld = op.old! + 1;
    } else {
      open.lines.push(edited[op.new! - 1]);
      nextNew = op.new! + 1;
    }
  }
  if (open) out.push(open);
  return out;
}

/** `head` with `regions` (in order, as `editRegions` gives them) applied. */
export function applyEdits(head: readonly string[], regions: readonly Pick<Region, 'start' | 'end' | 'lines'>[]): string[] {
  const out: string[] = [];
  let next = 1;
  for (const r of regions) {
    out.push(...head.slice(next - 1, r.start - 1), ...r.lines);
    next = r.end + 1;
  }
  out.push(...head.slice(next - 1));
  return out;
}

function hunkOf(hunks: readonly Hunk[], start: number, end: number): number {
  return hunks.findIndex((h) => h.start <= start && end <= h.end);
}

/**
 * `r` with at least one head line: an insertion takes in the line above it
 * when that line is in a hunk, else the line below when that one is, else
 * whichever exists.
 */
function anchored(r: Region, head: readonly string[], hunks: readonly Hunk[]): Region {
  if (r.end >= r.start || !head.length) return r;
  const above = r.start - 1;
  const below = r.start;
  const useAbove = above >= 1 && (hunkOf(hunks, above, above) >= 0 || below > head.length || hunkOf(hunks, below, below) < 0);
  return useAbove
    ? { start: above, end: above, lines: [head[above - 1], ...r.lines], at: r.at - 1 }
    : { start: below, end: below, lines: [...r.lines, head[below - 1]], at: r.at };
}

/** One suggestion spanning `regions` (in order), with the head lines between them kept. */
function span(head: readonly string[], regions: readonly Region[]): Suggestion {
  const start = regions[0].start;
  const end = regions[regions.length - 1].end;
  const lines: string[] = [];
  let next = start;
  for (const r of regions) {
    lines.push(...head.slice(next - 1, r.start - 1), ...r.lines);
    next = r.end + 1;
  }
  return { start, end, original: head.slice(start - 1, end), lines, at: regions[0].at };
}

/** How the edits from `head` to `edited` can be posted, given the `hunks` that take line comments. */
export function planSuggestion(head: readonly string[], edited: readonly string[], hunks: readonly Hunk[]): SuggestPlan {
  const regions = editRegions(head, edited).map((r) => anchored(r, head, hunks));
  if (!regions.length) return { kind: 'unchanged' };
  const outside = regions.filter((r) => r.end < r.start || hunkOf(hunks, r.start, r.end) < 0);
  if (outside.length) return { kind: 'outside', regions, outside, patch: unifiedPatch(head, edited) };
  const groups: Region[][] = [];
  let last = -1;
  for (const r of regions) {
    const h = hunkOf(hunks, r.start, r.end);
    if (h === last) groups[groups.length - 1].push(r);
    else groups.push([r]);
    last = h;
  }
  const suggestions = groups.map((g) => span(head, g));
  return suggestions.length === 1 ? { kind: 'one', suggestion: suggestions[0] } : { kind: 'split', suggestions };
}

/** Context lines around each change in a patch. */
const CONTEXT = 3;

/** A unified diff of `head` to `edited` (hunks only, no file header), each line ending in a newline. */
export function unifiedPatch(head: readonly string[], edited: readonly string[], context = CONTEXT): string {
  const ops = myers([...head], [...edited]);
  const changed = ops.map((o, i) => (o.kind === 'context' ? -1 : i)).filter((i) => i >= 0);
  if (!changed.length) return '';
  // Ranges of ops to show: each change with `context` ops around it, joined where they meet.
  const ranges: [number, number][] = [];
  for (const i of changed) {
    const from = Math.max(0, i - context);
    const to = Math.min(ops.length - 1, i + context);
    const prev = ranges[ranges.length - 1];
    if (prev && from <= prev[1] + 1) prev[1] = Math.max(prev[1], to);
    else ranges.push([from, to]);
  }
  let out = '';
  for (const [from, to] of ranges) {
    const shown = ops.slice(from, to + 1);
    const oldCount = shown.filter((o) => o.kind !== 'add').length;
    const newCount = shown.filter((o) => o.kind !== 'del').length;
    // A side's range starts at its first line shown, or (with none) after the last line before the hunk.
    const before = ops.slice(0, from);
    const oldStart = oldCount ? shown.find((o) => o.old !== null)!.old! : before.filter((o) => o.kind !== 'add').length;
    const newStart = newCount ? shown.find((o) => o.new !== null)!.new! : before.filter((o) => o.kind !== 'del').length;
    out += `@@ -${oldStart},${oldCount} +${newStart},${newCount} @@\n`;
    for (const o of shown) {
      const text = o.kind === 'add' ? edited[o.new! - 1] : head[o.old! - 1];
      out += (o.kind === 'add' ? '+' : o.kind === 'del' ? '-' : ' ') + text + '\n';
    }
  }
  return out;
}

/** A comment body with `patch` as a `diff` block after the text; the fence is longer than any backtick run in the patch. */
export function formatPatch(text: string, patch: string): string {
  const longest = Math.max(0, ...(patch.match(/`+/g) ?? []).map((r) => r.length));
  const fence = '`'.repeat(Math.max(3, longest + 1));
  const lead = text.trim() ? text.trimEnd() + '\n\n' : '';
  return `${lead}${fence}diff\n${patch}${fence}`;
}

/** The new-side lines of `blocks` (one file's, in order) that take line comments, as maximal runs: the diff's hunks. */
export function commentableRuns(blocks: readonly Block[]): Hunk[] {
  const out: Hunk[] = [];
  for (const b of blocks)
    for (const r of b.rows) {
      if (r.new === null || !r.commentableNew) continue;
      const last = out[out.length - 1];
      if (last && r.new === last.end + 1) last.end = r.new;
      else if (!last || r.new > last.end) out.push({ start: r.new, end: r.new });
    }
  return out;
}

/**
 * A file's lines without a trailing `\r`, and whether it ends its lines
 * with CRLF (most of its lines do). The editor works in `\n`, so plans are
 * made on these lines.
 */
export function normalizeEndings(lines: readonly string[]): { lines: string[]; crlf: boolean } {
  let cr = 0;
  const out = lines.map((l) => {
    if (!l.endsWith('\r')) return l;
    cr++;
    return l.slice(0, -1);
  });
  return { lines: out, crlf: cr > 0 && cr * 2 >= lines.length };
}

/** Suggested lines with the file's line ending: `\r` back on each for a CRLF file. */
export function withEnding(lines: readonly string[], crlf: boolean): string[] {
  return crlf ? lines.map((l) => l + '\r') : [...lines];
}

/**
 * The line comments a plan's suggestions post, in order: one for `one`,
 * one per hunk for `split` (the comment `text` with the first), none
 * otherwise. Each suggestion's lines take the file's ending (`crlf`).
 */
export function suggestionComments(plan: SuggestPlan, text: string, crlf: boolean): { start: number; end: number; body: string }[] {
  const suggestions = plan.kind === 'one' ? [plan.suggestion] : plan.kind === 'split' ? plan.suggestions : [];
  return suggestions.map((s, i) => ({ start: s.start, end: s.end, body: formatSuggestion(i === 0 ? text : '', withEnding(s.lines, crlf)) }));
}
